import React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, ChevronLeft, ChevronRight, HelpCircle, Maximize2, Minimize2, Search, Sparkles, X } from 'lucide-react';

export function fieldClass(isLight = true): string {
  return isLight
    ? 'w-full px-3 py-2.5 rounded-xl text-sm bg-white border border-neutral-200 text-neutral-900 placeholder:text-neutral-400 transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-400'
    : 'w-full px-3 py-2.5 rounded-xl text-sm bg-neutral-900 border border-neutral-700 text-white placeholder:text-neutral-500 transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400';
}

export function searchField(isLight = true): string {
  return isLight
    ? 'w-full pl-10 pr-3 py-2.5 rounded-xl text-sm bg-white border border-neutral-200 text-neutral-900 placeholder:text-neutral-400 transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-400'
    : 'w-full pl-10 pr-3 py-2.5 rounded-xl text-sm bg-neutral-900 border border-neutral-700 text-white placeholder:text-neutral-500 transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400';
}

export function selectField(isLight = true): string {
  return isLight
    ? 'px-3 py-2.5 rounded-xl text-sm bg-white border border-neutral-200 text-neutral-700 transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-400'
    : 'px-3 py-2.5 rounded-xl text-sm bg-neutral-900 border border-neutral-700 text-neutral-200 transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400';
}

export function labelClass(isLight = true): string {
  return isLight ? 'block text-sm font-medium mb-1.5 text-neutral-700' : 'block text-sm font-medium mb-1.5 text-neutral-300';
}

export function helperClass(isLight = true): string {
  return isLight ? 'mt-1 text-xs text-neutral-500' : 'mt-1 text-xs text-neutral-400';
}

export function SearchableSelect({
  label,
  value,
  options,
  onChange,
  isLight,
  placeholder = 'Select…',
  searchPlaceholder = 'Search',
}: {
  label: string;
  value: string;
  options: { id: string; label: string; hint?: string }[];
  onChange: (next: string) => void;
  isLight: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  const selected = options.find((option) => option.id === value);
  const filtered = options.filter((option) => {
    const haystack = `${option.label} ${option.hint || ''} ${option.id}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  return (
    <div ref={rootRef} className="relative">
      <p className={labelClass(isLight)}>{label}</p>
      <button
        type="button"
        onClick={() => setOpen((next) => !next)}
        className={`${fieldClass(isLight)} min-h-[46px] h-auto py-1.5 flex items-center justify-between gap-2 text-left`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="min-w-0 flex-1">
          {selected ? (
            <span className="block min-w-0">
              <span className="block truncate font-medium">{selected.label}</span>
              {selected.hint ? (
                <span className={`block text-[11px] truncate ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>{selected.hint}</span>
              ) : null}
            </span>
          ) : (
            <span className={isLight ? 'text-neutral-400' : 'text-neutral-500'}>{placeholder}</span>
          )}
        </span>
        <ChevronDown size={14} className={`shrink-0 ${open ? 'rotate-180' : ''} ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
      </button>
      {open ? (
        <div
          className={`absolute z-30 mt-1.5 w-full rounded-xl border shadow-xl overflow-hidden ${
            isLight ? 'bg-white border-neutral-200 shadow-neutral-900/10' : 'bg-neutral-900 border-neutral-700 shadow-black/40'
          }`}
        >
          <div className={`flex items-center gap-2 px-3 py-2.5 border-b ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
            <Search size={13} className={isLight ? 'text-neutral-400' : 'text-neutral-500'} />
            <input
              className={`w-full bg-transparent text-sm outline-none ${isLight ? 'text-neutral-900 placeholder:text-neutral-400' : 'text-white placeholder:text-neutral-500'}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              autoFocus
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1" role="listbox">
            {filtered.length ? (
              filtered.map((option) => {
                const checked = option.id === value;
                return (
                  <li key={option.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={checked}
                      className={`w-full flex items-start gap-2.5 px-3 py-2.5 text-sm text-left ${
                        isLight ? 'hover:bg-neutral-50 text-neutral-800' : 'hover:bg-neutral-800 text-neutral-200'
                      } ${checked ? (isLight ? 'bg-indigo-50/70' : 'bg-indigo-500/10') : ''}`}
                      onClick={() => {
                        onChange(option.id);
                        setOpen(false);
                      }}
                    >
                      <span
                        className={`mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center shrink-0 ${
                          checked
                            ? 'border-indigo-500 bg-indigo-600 text-white'
                            : isLight
                              ? 'border-neutral-300 bg-white'
                              : 'border-neutral-600 bg-neutral-900'
                        }`}
                      >
                        {checked ? <Check size={10} strokeWidth={3} /> : null}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{option.label}</span>
                        {option.hint ? (
                          <span className={`block text-[11px] truncate ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>{option.hint}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })
            ) : (
              <li className={`px-3 py-2.5 text-sm ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>No matches</li>
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function primaryBtn(extra = ''): string {
  return `inline-flex items-center justify-center gap-1.5 h-9 px-3.5 rounded-xl text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm shadow-indigo-600/20 disabled:opacity-50 disabled:pointer-events-none transition-colors ${extra}`;
}

export function ghostBtn(isLight = true): string {
  return isLight
    ? 'inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-xl text-sm font-medium border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 hover:border-neutral-300 disabled:opacity-40 disabled:pointer-events-none transition-colors'
    : 'inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-xl text-sm font-medium border border-neutral-700 bg-neutral-900 text-neutral-200 hover:bg-neutral-800 hover:border-neutral-600 disabled:opacity-40 disabled:pointer-events-none transition-colors';
}

export function dangerBtn(): string {
  return 'inline-flex items-center justify-center gap-1.5 h-9 px-3.5 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 shadow-sm shadow-red-600/15 transition-colors';
}

export function tableShell(isLight = true): string {
  return isLight
    ? 'overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm'
    : 'overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-900 shadow-sm';
}

export function tableHeadClass(isLight = true): string {
  return isLight ? 'text-neutral-500 bg-neutral-50/90' : 'text-neutral-400 bg-neutral-950/90';
}

export function thClass(): string {
  return 'px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap';
}

export function trClass(isLight = true): string {
  return isLight
    ? 'border-t border-neutral-100 hover:bg-indigo-50/40 transition-colors'
    : 'border-t border-neutral-800 hover:bg-indigo-500/10 transition-colors';
}

export function tdClass(): string {
  return 'px-5 py-3.5 align-middle';
}

export function panelClass(isLight = true): string {
  return isLight
    ? 'rounded-2xl border border-neutral-200 bg-white shadow-sm'
    : 'rounded-2xl border border-neutral-800 bg-neutral-900 shadow-sm';
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '•';
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function Avatar({ name, tone = 'indigo' }: { name: string; tone?: 'indigo' | 'violet' | 'sky' | 'emerald' }) {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-700',
    violet: 'bg-violet-50 text-violet-700',
    sky: 'bg-sky-50 text-sky-700',
    emerald: 'bg-emerald-50 text-emerald-700',
  };
  return (
    <span className={`h-10 w-10 rounded-xl text-xs font-semibold flex items-center justify-center shrink-0 ${tones[tone]}`}>
      {initials(name)}
    </span>
  );
}

export function IconWell({ children, isLight = true }: { children: React.ReactNode; isLight?: boolean }) {
  return (
    <span
      className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
        isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'
      }`}
    >
      {children}
    </span>
  );
}

export function SearchBar({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative flex-1 min-w-[200px]">
      <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
      <input className={searchField()} placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

export function HelpHint({ text }: { text: string }) {
  return (
    <span className="inline-flex items-center text-neutral-400" title={text}>
      <HelpCircle size={13} />
    </span>
  );
}

export function FormSection({
  title,
  description,
  isLight,
  children,
  action,
}: {
  title: string;
  description?: string;
  isLight: boolean;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{title}</h3>
          {description ? <p className={`text-sm mt-1 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{description}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function FormRail({ title, children, isLight }: { title: string; isLight: boolean; children: React.ReactNode }) {
  return (
    <aside className="space-y-4 lg:sticky lg:top-0">
      <section className={`${panelClass(isLight)} p-5`}>
        <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{title}</h3>
        <div className="mt-3">{children}</div>
      </section>
    </aside>
  );
}

export function FormStack({ children }: { children: React.ReactNode }) {
  return <div className="grid lg:grid-cols-[minmax(0,1fr)_260px] gap-6 items-start">{children}</div>;
}

export function HorizontalStepper({
  steps,
  currentIndex,
  onSelect,
  isLight = true,
}: {
  steps: { id: string; label: string }[];
  currentIndex: number;
  onSelect?: (index: number) => void;
  isLight?: boolean;
}) {
  return (
    <ol className="flex items-center gap-1 overflow-x-auto pb-1">
      {steps.map((step, index) => {
        const done = index < currentIndex;
        const active = index === currentIndex;
        return (
          <li key={step.id} className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => onSelect?.(index)}
              className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                done
                  ? isLight
                    ? 'text-emerald-700 bg-emerald-50'
                    : 'text-emerald-300 bg-emerald-500/15'
                  : active
                    ? isLight
                      ? 'text-indigo-700 bg-indigo-50'
                      : 'text-indigo-300 bg-indigo-500/15'
                    : isLight
                      ? 'text-neutral-400 hover:text-neutral-600'
                      : 'text-neutral-500 hover:text-neutral-300'
              }`}
            >
              <span
                className={`h-5 w-5 rounded-full text-[10px] flex items-center justify-center ${
                  done
                    ? 'bg-emerald-500 text-white'
                    : active
                      ? 'bg-indigo-600 text-white'
                      : isLight
                        ? 'bg-neutral-200 text-neutral-600'
                        : 'bg-neutral-800 text-neutral-300'
                }`}
              >
                {done ? <Check size={11} /> : index + 1}
              </span>
              {step.label}
            </button>
            {index < steps.length - 1 ? <span className={`w-6 h-px ${isLight ? 'bg-neutral-200' : 'bg-neutral-800'}`} /> : null}
          </li>
        );
      })}
    </ol>
  );
}

export const TABLE_PAGE_SIZE = 10;

export function pageSlice<T>(items: T[], page: number, pageSize = TABLE_PAGE_SIZE): T[] {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  return items.slice((current - 1) * pageSize, current * pageSize);
}

export function TablePager({
  page,
  total,
  pageSize = TABLE_PAGE_SIZE,
  isLight,
  onPage,
  noun,
}: {
  page: number;
  total: number;
  pageSize?: number;
  isLight: boolean;
  onPage: (page: number) => void;
  noun: string;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  return (
    <div className="flex items-center justify-between gap-3 px-1 py-2">
      <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-500'}`}>
        {total} {noun}
        {total === 1 ? '' : 's'}
      </p>
      <div className="flex items-center gap-2">
        <button type="button" className={ghostBtn(isLight)} disabled={current <= 1} onClick={() => onPage(current - 1)}>
          <ChevronLeft size={14} />
        </button>
        <span className={`text-xs tabular-nums ${isLight ? 'text-neutral-600' : 'text-neutral-400'}`}>
          {current} / {totalPages}
        </span>
        <button type="button" className={ghostBtn(isLight)} disabled={current >= totalPages} onClick={() => onPage(current + 1)}>
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

export function GatewayDrawer({
  open,
  title,
  subtitle,
  eyebrow,
  icon,
  status,
  isLight,
  onClose,
  children,
  footer,
  flush,
  size = 'xl',
  maximizable = false,
  maximized,
  onMaximizedChange,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  eyebrow?: string;
  icon?: React.ReactNode;
  status?: { label: string; tone?: 'success' | 'neutral' | 'warn' };
  isLight: boolean;
  widthClass?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  flush?: boolean;
  size?: 'lg' | 'xl' | 'full';
  maximizable?: boolean;
  maximized?: boolean;
  onMaximizedChange?: (next: boolean) => void;
}) {
  const [internalMax, setInternalMax] = React.useState(false);
  React.useEffect(() => {
    if (!open) setInternalMax(false);
  }, [open]);
  const isMax = maximized ?? internalMax;
  const setMax = (next: boolean) => {
    onMaximizedChange?.(next);
    if (maximized === undefined) setInternalMax(next);
  };
  const resolvedSize = isMax ? 'full' : size;
  if (!open) return null;
  const statusCls =
    status?.tone === 'success'
      ? isLight
        ? 'bg-emerald-50 text-emerald-700'
        : 'bg-emerald-500/15 text-emerald-300'
      : status?.tone === 'warn'
        ? isLight
          ? 'bg-amber-50 text-amber-700'
          : 'bg-amber-500/15 text-amber-300'
        : isLight
          ? 'bg-neutral-100 text-neutral-600'
          : 'bg-neutral-800 text-neutral-300';
  const shellClass =
    resolvedSize === 'full'
      ? 'fixed inset-0 z-[170] flex items-stretch justify-center p-2 md:p-3'
      : 'fixed inset-0 z-[170] flex items-center justify-center p-3 md:p-8';
  const dialogClass =
    resolvedSize === 'full'
      ? `relative flex flex-col w-full h-full max-w-none max-h-none rounded-xl overflow-hidden shadow-2xl ${
          isLight ? 'border border-neutral-200 bg-white shadow-neutral-900/15' : 'border border-neutral-800 bg-neutral-950 shadow-black/40'
        }`
      : `relative flex flex-col w-full ${size === 'lg' ? 'max-w-3xl' : 'max-w-5xl'} max-h-[92vh] rounded-2xl overflow-hidden shadow-2xl ${
          isLight ? 'border border-neutral-200 bg-white shadow-neutral-900/15' : 'border border-neutral-800 bg-neutral-950 shadow-black/40'
        }`;

  return createPortal(
    <div className={shellClass}>
      <button type="button" className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[3px]" aria-label="Close overlay" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="gateway-popup-title"
        className={dialogClass}
      >
        <header
          className={`px-6 pt-5 border-b shrink-0 ${
            isLight
              ? 'border-neutral-100 bg-gradient-to-b from-indigo-50/70 to-white'
              : 'border-neutral-800 bg-gradient-to-b from-indigo-500/10 to-neutral-950'
          }`}
        >
          {eyebrow ? (
            <p className={`text-[11px] font-medium uppercase tracking-wider mb-2 ${isLight ? 'text-indigo-500/80' : 'text-indigo-300/80'}`}>
              {eyebrow}
            </p>
          ) : null}
          <div className="flex items-start justify-between gap-4 pb-4">
            <div className="flex items-start gap-3 min-w-0">
              {icon ? <IconWell isLight={isLight}>{icon}</IconWell> : null}
              <div className="min-w-0">
                <h2 id="gateway-popup-title" className={`text-lg font-semibold tracking-tight ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                  {title}
                </h2>
                {subtitle ? <p className={`text-sm mt-1 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{subtitle}</p> : null}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {status ? (
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusCls}`}>{status.label}</span>
              ) : null}
              {maximizable ? (
                <button
                  type="button"
                  onClick={() => setMax(!isMax)}
                  className={`p-1.5 rounded-lg ${isLight ? 'hover:bg-white text-neutral-400' : 'hover:bg-neutral-800 text-neutral-500'}`}
                  aria-label={isMax ? 'Restore' : 'Maximize'}
                >
                  {isMax ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className={`p-1.5 rounded-lg ${isLight ? 'hover:bg-white text-neutral-400' : 'hover:bg-neutral-800 text-neutral-500'}`}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </header>
        <div
          className={
            flush
              ? 'flex-1 min-h-0 overflow-hidden'
              : `flex-1 min-h-0 overflow-y-auto px-6 py-6 ${isLight ? 'bg-[#F8F8FA]' : 'bg-neutral-950'}`
          }
        >
          {children}
        </div>
        {footer ? (
          <footer
            className={`px-6 py-3.5 border-t flex items-center justify-end gap-2 shrink-0 ${
              isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-950'
            }`}
          >
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

export function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  isLight,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  isLight: boolean;
}) {
  return (
    <label className="flex items-start justify-between gap-6 py-3">
      <span>
        <span className={`block text-sm font-medium ${isLight ? 'text-neutral-800' : 'text-neutral-200'}`}>{label}</span>
        {hint ? <span className={`block text-xs mt-0.5 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{hint}</span> : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full shrink-0 transition-colors ${checked ? 'bg-indigo-600' : isLight ? 'bg-neutral-300' : 'bg-neutral-700'}`}
      >
        <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </button>
    </label>
  );
}

export function CheckRow({
  label,
  checked,
  onChange,
  isLight,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  isLight: boolean;
}) {
  return (
    <label className={`flex items-center gap-2 text-sm ${isLight ? 'text-neutral-700' : 'text-neutral-300'}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className={`rounded text-indigo-600 focus:ring-indigo-500 ${isLight ? 'border-neutral-300' : 'border-neutral-600 bg-neutral-900'}`}
      />
      {label}
    </label>
  );
}

export function KpiCard({
  label,
  value,
}: {
  label: string;
  value: number | string;
  isLight?: boolean;
  tone?: 'default' | 'success' | 'muted';
}) {
  return (
    <div>
      <p className="text-xl font-semibold tabular-nums tracking-tight text-neutral-900">{value}</p>
      <p className="text-xs mt-0.5 text-neutral-500">{label}</p>
    </div>
  );
}

export function MaximizableTextarea({
  label,
  value,
  onChange,
  placeholder,
  hint,
  isLight,
  rows = 6,
  mono,
  copilot,
  onCopilot,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  hint?: string;
  isLight: boolean;
  rows?: number;
  mono?: boolean;
  copilot?: boolean;
  onCopilot?: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const areaClass = `${fieldClass(isLight)} ${mono ? 'font-mono text-xs' : ''}`;

  const editor = (expanded: boolean) => (
    <textarea
      className={`${areaClass} ${expanded ? 'min-h-[60vh]' : ''}`}
      rows={expanded ? 24 : rows}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  );

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <label className={labelClass(isLight) + ' mb-0'}>{label}</label>
        <div className="flex items-center gap-1">
          {copilot ? (
            <button
              type="button"
              className={`${ghostBtn(isLight)} h-8 px-2`}
              onClick={onCopilot}
              aria-label="Generate with copilot"
              title="Copilot will generate this schema later"
            >
              <Sparkles size={14} />
            </button>
          ) : null}
          <button
            type="button"
            className={`${ghostBtn(isLight)} h-8 px-2`}
            onClick={() => setOpen(true)}
            aria-label={`Maximize ${label}`}
          >
            <Maximize2 size={14} />
          </button>
        </div>
      </div>
      {editor(false)}
      {hint ? <p className={`text-[11px] mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{hint}</p> : null}
      {open
        ? createPortal(
            <div className="fixed inset-0 z-[240] flex items-center justify-center p-4">
              <button type="button" className="absolute inset-0 bg-neutral-950/50 backdrop-blur-[3px]" aria-label="Close" onClick={() => setOpen(false)} />
              <div className={`relative w-full max-w-5xl max-h-[92vh] overflow-auto rounded-2xl border p-5 shadow-2xl ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{label}</p>
                  <div className="flex items-center gap-2">
                    {copilot ? (
                      <button type="button" className={ghostBtn(isLight)} onClick={onCopilot}>
                        <Sparkles size={14} /> Copilot
                      </button>
                    ) : null}
                    <button type="button" className={ghostBtn(isLight)} onClick={() => setOpen(false)}>
                      <X size={14} /> Close
                    </button>
                  </div>
                </div>
                {editor(true)}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
